import { useEffect, useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { DataTable } from "@/components/common/DataTable";
import { Loader } from "@/components/common/Loader";
import { shortDate } from "@/lib/format";
import { openPdf } from "@/lib/pdf";
import api from "@/lib/api";
import { FileText, ArrowRight, CheckCircle2, XCircle, Landmark, Code2, Download, X, Send, Radio, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BLEU = "#0055A4";

export default function FranceEInvoicing() {
  const [regimes, setRegimes] = useState([]);
  const [site, setSite] = useState("EU");
  const [s, setS] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billable, setBillable] = useState([]);
  const [batches, setBatches] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/einvoicing/regimes").then((r) => { setRegimes(r.data); if (r.data[0]) setSite(r.data[0].site_id); });
    api.get("/einvoicing/ereport/pipeline").then((r) => setPipeline(r.data)).catch(() => {});
  }, []);
  const load = async (sc) => {
    setLoading(true);
    try {
      const [st, inv, bill, bat] = await Promise.all([
        api.get(`/einvoicing/status?site=${sc}`), api.get(`/einvoicing/invoices?site=${sc}`), api.get(`/einvoicing/billable?site=${sc}`), api.get(`/einvoicing/ereport/batches?site=${sc}`)]);
      setS(st.data); setInvoices(inv.data); setBillable(bill.data); setBatches(bat.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (site) load(site); }, [site]);

  const money = (v) => `${s?.symbol || "£"}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const generate = async (o) => {
    try { const { data } = await api.post("/einvoicing/invoice", { sales_order_id: o.id }); toast.success(`E-invoice ${data.invoice_no} issued · ${money(data.ttc)}`); load(site); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const advance = async (ei) => {
    try { const { data } = await api.post(`/einvoicing/invoice/${ei.id}/advance`); toast.success(`Status → ${s.statuses[data.lifecycle] || data.lifecycle}`); load(site); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot advance"); }
  };

  const [xmlModal, setXmlModal] = useState(null); // {ei, fmt, xml, validation, loading}
  const fetchXml = async (ei, fmt) => {
    setXmlModal((m) => ({ ...m, fmt, xml: "", loading: true }));
    try {
      const { data } = await api.get(`/einvoicing/invoice/${ei.id}/xml?fmt=${fmt}`, { responseType: "text" });
      setXmlModal((m) => ({ ...m, fmt, xml: data, loading: false }));
    } catch (e) { toast.error("Failed to load XML"); setXmlModal((m) => ({ ...m, loading: false })); }
  };
  const openXml = async (ei) => {
    setXmlModal({ ei, fmt: "cii", xml: "", validation: null, loading: true });
    try {
      const { data: v } = await api.get(`/einvoicing/invoice/${ei.id}/validate`);
      setXmlModal((m) => ({ ...m, validation: v }));
    } catch { /* ignore */ }
    fetchXml(ei, "cii");
  };
  const downloadBlob = async (ei, fmt) => {
    try {
      const isFx = fmt === "facturx";
      const path = isFx ? `/einvoicing/invoice/${ei.id}/facturx` : `/einvoicing/invoice/${ei.id}/xml?fmt=${fmt}&download=true`;
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = isFx ? `FacturX_${ei.invoice_no}.pdf` : `${ei.invoice_no}_${fmt}.xml`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${isFx ? "Factur-X PDF/A-3" : fmt.toUpperCase() + " XML"} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  // --- PDP e-reporting simulator ---
  const [pdp, setPdp] = useState(null); // selected batch (modal)
  const [pdpBusy, setPdpBusy] = useState(false);
  const createBatch = async () => {
    setPdpBusy(true);
    try {
      const { data } = await api.post("/einvoicing/ereport/batch", { site });
      toast.success(`E-report batch ${data.transmission_id} created · ${data.accepted} accepted`);
      setPdp(data); load(site);
    } catch (e) { toast.error(e?.response?.data?.detail || "No invoices to e-report"); }
    finally { setPdpBusy(false); }
  };
  const advanceBatch = async (b) => {
    setPdpBusy(true);
    try {
      const { data } = await api.post(`/einvoicing/ereport/batch/${b.id}/advance`);
      setPdp(data); load(site);
      const last = data.flow_log[data.flow_log.length - 1];
      toast.success(`Status → ${last.en} · ${last.ack}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot advance"); }
    finally { setPdpBusy(false); }
  };
  const stageLabel = (code) => (pipeline.find((p) => p.code === code) || {}).en || code;

  if (loading || !s) return <Loader label="Loading e-invoicing compliance…" />;

  const billCols = [
    { key: "order_no", label: "Order", render: (r) => <span className="font-mono text-xs text-[#0055A4]">{r.order_no}</span> },
    { key: "customer_name", label: "Customer", render: (r) => (<div><div>{r.customer_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.customer_code}</div></div>) },
    { key: "net_value", label: "Net", render: (r) => <span className="font-mono text-emerald-600">{money(r.net_value)}</span> },
    { key: "order_date", label: "Date", render: (r) => shortDate(r.order_date) },
    { key: "actions", label: "", render: (r) => (r.has_einvoice
      ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"><CheckCircle2 size={13} /> Issued</span>
      : <button data-testid={`ei-gen-${r.order_no}`} onClick={() => generate(r)} className="inline-flex items-center gap-1 rounded-md bg-[#0055A4] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#004080]"><FileText size={13} /> Issue e-invoice</button>) },
  ];
  const eiCols = [
    { key: "invoice_no", label: "Invoice", render: (r) => <span className="font-mono text-xs text-[#0055A4]">{r.invoice_no}</span> },
    { key: "buyer", label: "Buyer (Tax ID)", render: (r) => (<div><div>{r.buyer?.name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.buyer?.siret} · {r.buyer?.tax_id}</div></div>) },
    { key: "format", label: "Format", render: (r) => <span className="text-[11px]">{r.format}</span> },
    { key: "ttc", label: "Total", render: (r) => <span className="font-mono font-semibold">{money(r.ttc)}</span> },
    { key: "lifecycle", label: "Status", render: (r) => <span className="rounded bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700">{s.statuses[r.lifecycle] || r.lifecycle}</span> },
    { key: "actions", label: "", render: (r) => (
      <div className="flex flex-wrap gap-1.5">
        <button data-testid={`ei-pdf-${r.invoice_no}`} onClick={() => openPdf(`/einvoicing/invoice/${r.id}/pdf`)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] hover:border-[#0055A4]"><FileText size={13} /> PDF</button>
        <button data-testid={`ei-xml-${r.invoice_no}`} onClick={() => openXml(r)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] hover:border-[#0055A4]"><Code2 size={13} /> XML</button>
        <button data-testid={`ei-adv-${r.invoice_no}`} onClick={() => advance(r)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#0055A4]"><ArrowRight size={13} /> Advance</button>
      </div>) },
  ];

  return (
    <div data-testid="einvoicing-page">
      <PageHeader title="E-Invoicing & Tax Compliance" subtitle="Country-specific electronic invoicing regimes — France (Factur-X / UBL / CII XML, EN 16931, PDP 2026-27), USA (Peppol). Structured XML with business-rule validation." icon={Landmark} accent={BLEU}>
        <select data-testid="ei-country-select" value={site} onChange={(e) => setSite(e.target.value)} className="rounded-lg border border-[#e5e9f0] bg-white px-3 py-2 text-sm">
          {regimes.map((r) => <option key={r.site_id} value={r.site_id}>{r.country} · {r.name}</option>)}
        </select>
      </PageHeader>

      <div className="mb-6 overflow-hidden rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
        <div className="border-b border-[#e5e9f0] px-5 py-3 text-sm font-semibold uppercase tracking-wide text-[#32363a]">{s.country} — mandate timeline</div>
        <div className="grid gap-4 p-5 md:grid-cols-3">
          {s.mandate.map((t, i) => (
            <div key={i} data-testid={`ei-timeline-${i}`} className={`rounded-lg border p-4 ${t.done ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
              <div className="flex items-center justify-between"><span className="font-mono text-xs font-semibold">{t.date}</span><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${t.done ? "bg-emerald-200 text-emerald-800" : "bg-amber-200 text-amber-800"}`}>{t.done ? "LIVE" : "UPCOMING"}</span></div>
              <div className="mt-1 text-sm font-semibold text-[#32363a]">{t.title}</div>
              <div className="mt-1 text-xs text-[#6a6d70]">{t.body}</div>
            </div>))}
        </div>
        <div className="border-t border-[#e5e9f0] bg-[#f7f8fa] px-5 py-3 text-xs text-[#6a6d70]">
          Issuer: <span className="font-medium text-[#32363a]">{s.seller.name}</span> · {s.seller.siret || s.seller.siren} {s.seller.tva ? `· ${s.seller.tva}` : ""} — {s.seller.address}
          <div className="mt-1">Channels: {s.channels.join(" · ")} · Formats: {s.formats.join(", ")}</div>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="ei-kpi-issued" label="Invoices Issued" value={s.issued} accent={BLEU} />
        <KpiCard testId="ei-kpi-transit" label="In Transit" value={s.in_transit} accent="#6366F1" />
        <KpiCard testId="ei-kpi-accepted" label="Accepted / Paid" value={s.accepted} accent="#10B981" />
        <KpiCard testId="ei-kpi-ttc" label="Total Issued" value={money(s.ttc_total)} accent="#EF4135" />
      </div>

      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">{s.country} orders to invoice</h3>
      <DataTable testId="ei-billable-table" columns={billCols} rows={billable} />
      <h3 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Issued e-invoices</h3>
      {invoices.length === 0 ? <div className="rounded-lg border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No e-invoices issued yet.</div> : <DataTable testId="ei-invoices-table" columns={eiCols} rows={invoices} />}

      {xmlModal && (
        <div data-testid="ei-xml-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setXmlModal(null)}>
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#e5e9f0] px-5 py-3">
              <div>
                <div className="text-sm font-semibold text-[#32363a]">Structured e-invoice · {xmlModal.ei.invoice_no}</div>
                <div className="text-[11px] text-[#6a6d70]">EN 16931 semantic model — France mandate formats</div>
              </div>
              <button data-testid="ei-xml-close" onClick={() => setXmlModal(null)} className="rounded-md p-1 text-[#6a6d70] hover:bg-[#eef1f5]"><X size={18} /></button>
            </div>

            {xmlModal.validation && (
              <div className="border-b border-[#e5e9f0] bg-[#f7f8fa] px-5 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-semibold ${xmlModal.validation.compliant ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                    {xmlModal.validation.compliant ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                    EN 16931 — {xmlModal.validation.passed}/{xmlModal.validation.total} rules passed
                  </span>
                </div>
                <div className="grid max-h-32 gap-1 overflow-y-auto md:grid-cols-2">
                  {xmlModal.validation.rules.map((rl) => (
                    <div key={rl.id} data-testid={`ei-rule-${rl.id}`} className="flex items-start gap-1.5 text-[11px]">
                      {rl.status === "pass" ? <CheckCircle2 size={12} className="mt-0.5 shrink-0 text-emerald-500" /> : <XCircle size={12} className="mt-0.5 shrink-0 text-red-500" />}
                      <span className="text-[#32363a]"><span className="font-mono font-semibold">{rl.id}</span> {rl.name}{rl.detail ? <span className="text-[#6a6d70]"> · {rl.detail}</span> : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 border-b border-[#e5e9f0] px-5 py-2">
              {[["cii", "UN/CEFACT CII"], ["ubl", "UBL 2.1"]].map(([f, lbl]) => (
                <button key={f} data-testid={`ei-fmt-${f}`} onClick={() => fetchXml(xmlModal.ei, f)} className={`rounded-md px-3 py-1 text-[11px] font-medium ${xmlModal.fmt === f ? "bg-[#0055A4] text-white" : "border border-[#c8cfda] bg-white text-[#32363a] hover:border-[#0055A4]"}`}>{lbl}</button>
              ))}
              <div className="ml-auto flex gap-1.5">
                <button data-testid="ei-dl-xml" onClick={() => downloadBlob(xmlModal.ei, xmlModal.fmt)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2.5 py-1 text-[11px] hover:border-[#0055A4]"><Download size={13} /> {xmlModal.fmt.toUpperCase()} XML</button>
                <button data-testid="ei-dl-facturx" onClick={() => downloadBlob(xmlModal.ei, "facturx")} className="inline-flex items-center gap-1 rounded-md bg-[#0055A4] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#004080]"><Download size={13} /> Factur-X PDF/A-3</button>
              </div>
            </div>

            <div className="flex-1 overflow-auto bg-[#1e1e2e] p-4">
              {xmlModal.loading ? <div className="text-xs text-[#a9b1d6]">Generating XML…</div> :
                <pre data-testid="ei-xml-preview" className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-[#c8d3f5]">{xmlModal.xml}</pre>}
            </div>
          </div>
        </div>
      )}

      {site === "EU" && (
        <div className="mt-10">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">E-Reporting to PDP / PPF</h3>
              <p className="text-xs text-[#6a6d70]">Batch issued invoices, transmit them to the Partner Dematerialisation Platform and follow the DGFiP acknowledgement lifecycle.</p>
            </div>
            <button data-testid="pdp-new-batch" disabled={pdpBusy} onClick={createBatch} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0055A4] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#004080] disabled:opacity-60">
              {pdpBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} New e-report batch
            </button>
          </div>
          {batches.length === 0 ? (
            <div className="rounded-lg border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No e-report batches yet. Click “New e-report batch” to transmit all un-reported invoices to the PDP.</div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#e5e9f0] bg-white shadow-sm">
              <table className="w-full min-w-[640px] text-sm" data-testid="pdp-batches-table">
                <thead className="bg-[#f7f8fa] text-left text-[11px] uppercase tracking-wide text-[#6a6d70]">
                  <tr><th className="px-4 py-2.5">Transmission ID</th><th className="px-4 py-2.5">Invoices</th><th className="px-4 py-2.5">Value</th><th className="px-4 py-2.5">Current status</th><th className="px-4 py-2.5">Created</th><th className="px-4 py-2.5"></th></tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} data-testid={`pdp-row-${b.transmission_id}`} className="border-t border-[#eef1f5] hover:bg-[#f7f8fa]">
                      <td className="px-4 py-2.5 font-mono text-xs text-[#0055A4]">{b.transmission_id}</td>
                      <td className="px-4 py-2.5"><span className="text-emerald-600">{b.accepted} ✓</span>{b.rejected > 0 && <span className="ml-2 text-red-500">{b.rejected} ✕</span>}</td>
                      <td className="px-4 py-2.5 font-mono">{b.symbol}{Number(b.ttc_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="px-4 py-2.5"><span className={`rounded px-2 py-0.5 text-[11px] font-medium ${b.final ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>{stageLabel(b.status)}</span></td>
                      <td className="px-4 py-2.5 text-xs text-[#6a6d70]">{shortDate(b.created_at)}</td>
                      <td className="px-4 py-2.5 text-right"><button data-testid={`pdp-view-${b.transmission_id}`} onClick={() => setPdp(b)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2.5 py-1 text-[11px] hover:border-[#0055A4]"><Radio size={12} /> Track</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {pdp && (
        <div data-testid="pdp-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPdp(null)}>
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[#e5e9f0] px-5 py-3">
              <div>
                <div className="text-sm font-semibold text-[#32363a]">PDP e-reporting · {pdp.transmission_id}</div>
                <div className="text-[11px] text-[#6a6d70]">{pdp.channel} · {pdp.accepted} accepted{pdp.rejected > 0 ? ` · ${pdp.rejected} rejected (failed EN 16931 checks)` : ""}</div>
              </div>
              <button data-testid="pdp-close" onClick={() => setPdp(null)} className="rounded-md p-1 text-[#6a6d70] hover:bg-[#eef1f5]"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-auto p-5">
              <ol className="relative border-l-2 border-[#e5e9f0] pl-6">
                {pipeline.map((st, i) => {
                  const logEntry = (pdp.flow_log || []).find((l) => l.code === st.code);
                  const done = i <= (pdp.stage_index ?? 0);
                  const current = i === (pdp.stage_index ?? 0);
                  return (
                    <li key={st.code} data-testid={`pdp-stage-${st.code}`} className="mb-6 last:mb-0">
                      <span className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-[#0055A4]" : "bg-[#c8cfda]"} ${current && !pdp.final ? "ring-4 ring-sky-100" : ""}`}>
                        {done ? <CheckCircle2 size={12} className="text-white" /> : <span className="text-[9px] font-bold text-white">{i + 1}</span>}
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-sm font-semibold ${done ? "text-[#32363a]" : "text-[#9aa0a6]"}`}>{st.en}</span>
                        <span className="text-[11px] italic text-[#9aa0a6]">{st.fr}</span>
                        {logEntry && <span className="rounded bg-[#eef1f5] px-1.5 py-0.5 font-mono text-[10px] text-[#0055A4]">ACK {logEntry.ack}</span>}
                      </div>
                      <div className={`mt-0.5 text-xs ${done ? "text-[#6a6d70]" : "text-[#c0c4c9]"}`}>{st.desc}</div>
                      {logEntry && <div className="mt-0.5 text-[10px] text-[#9aa0a6]">{new Date(logEntry.ts).toLocaleString()}</div>}
                    </li>
                  );
                })}
              </ol>

              <div className="mt-5 rounded-lg border border-[#e5e9f0] bg-[#f7f8fa] p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">Invoices in this batch</div>
                <div className="flex flex-wrap gap-1.5">
                  {(pdp.invoices || []).map((iv) => (
                    <span key={iv.id} data-testid={`pdp-inv-${iv.invoice_no}`} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 font-mono text-[11px] ${iv.status === "ACCEPTED" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {iv.status === "ACCEPTED" ? <CheckCircle2 size={11} /> : <XCircle size={11} />}{iv.invoice_no} · {iv.passed}/{iv.total}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-[#e5e9f0] px-5 py-3">
              <span className="text-xs text-[#6a6d70]">{pdp.final ? "Lifecycle complete — payment e-reported to DGFiP." : `Current: ${stageLabel(pdp.status)}`}</span>
              <button data-testid="pdp-advance" disabled={pdp.final || pdpBusy} onClick={() => advanceBatch(pdp)} className="inline-flex items-center gap-1.5 rounded-lg bg-[#0055A4] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#004080] disabled:opacity-50">
                {pdpBusy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} {pdp.final ? "Completed" : "Advance status"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
